import React, { useEffect, useState } from 'react';
import { useAuth } from '../App';
import config from '../config/api';

const MemberWallet = ({ userId }) => {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      console.log('No userId provided to MemberWallet');
      return;
    }
    console.log('Loading wallet for userId:', userId);
    setLoading(true);
    setError(null);
    
    Promise.all([
      fetch(`${config.WALLET_API_URL}/member/${userId}/balance`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }),
      fetch(`${config.WALLET_API_URL}/member/${userId}/history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
    ])
      .then(async ([balanceRes, historyRes]) => {
        console.log('Balance response status:', balanceRes.status);
        console.log('History response status:', historyRes.status);
        
        if (!balanceRes.ok) {
          const errorText = await balanceRes.text();
          console.error('Balance error:', errorText);
          throw new Error(`Không thể lấy số dư (${balanceRes.status}): ${errorText}`);
        }
        if (!historyRes.ok) {
          const errorText = await historyRes.text();
          console.error('History error:', errorText);
          throw new Error(`Không thể lấy lịch sử giao dịch (${historyRes.status}): ${errorText}`);
        }
        
        const balanceData = await balanceRes.json();
        const historyData = await historyRes.json();
        
        console.log('Balance data:', balanceData);
        console.log('History data:', historyData);
        
        setBalance(balanceData);
        setTransactions(Array.isArray(historyData) ? historyData : []);
        setError(null);
      })
      .catch(err => {
        console.error('Wallet loading error:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="flex items-center justify-center p-8"><div className="text-gray-600">Đang tải ví thành viên #{userId}...</div></div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">Lỗi: {error}</div>;
  if (!balance) return <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-600">Không tìm thấy ví cho thành viên #{userId}.</div>;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-4 flex items-center">
        <span className="mr-2">💰</span>
        Ví thành viên #{userId}
      </h2>
      
      {/* Wallet Balance */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-700">Số dư hiện tại:</span>
          <span className="text-green-600 font-bold text-lg">
            {balance?.balance !== undefined ? 
              new Intl.NumberFormat('vi-VN').format(balance.balance) : 
              '0'
            } VND
          </span>
        </div>
        {balance?.locked_amount > 0 && (
          <div className="flex items-center justify-between mt-2">
            <span className="font-medium text-gray-700">Số tiền bị khóa:</span>
            <span className="text-red-600 font-bold">
              {new Intl.NumberFormat('vi-VN').format(balance.locked_amount)} VND
            </span>
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="mr-2">📋</span>
          Lịch sử giao dịch ({transactions.length})
        </h3>
        
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-2">📝</div>
            <div>Chưa có giao dịch nào</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-4 py-2 text-left">Mã GD</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Thời gian</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Loại</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Số tiền</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Mô tả</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2 font-mono">
                      {tx.transaction_code || `#${tx.id}`}
                    </td>
                    <td className="border border-gray-200 px-4 py-2 text-sm">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '-'}
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        tx.type === 'DEPOSIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {tx.type === 'DEPOSIT' ? 'Nạp tiền' : 'Rút tiền'}
                      </span>
                    </td>
                    <td className="border border-gray-200 px-4 py-2 font-mono">
                      <span className={tx.type === 'WITHDRAW' ? 'text-red-600' : 'text-green-600'}>
                        {tx.type === 'WITHDRAW' ? '-' : '+'}
                        {new Intl.NumberFormat('vi-VN').format(tx.amount)} VND
                      </span>
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      {tx.description || '-'}
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      {tx.approved_by ? (
                        <span className="text-green-600 font-semibold">Đã duyệt</span>
                      ) : tx.rejected_by ? (
                        <span className="text-red-600 font-semibold">Đã từ chối</span>
                      ) : (
                        <span className="text-yellow-600 font-semibold">Chờ duyệt</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberWallet;
